// CommandPalette - universal ⌘K overlay across the entire app.
//
// Opens via ⌘K (or Ctrl+K) anywhere. Type anything → live-filtered
// results across:
//   • PEOPLE - every person in the system, matched by name or DOB
//     (uses /api/today/search). Recent people surface first when the
//     query is empty.
//   • HOW TO - staff handbook topics + categories.
//   • ACTIONS - top-level navigation + the few special quick actions.
//
// Arrow keys to navigate, Enter to execute, Esc to dismiss. Recently-
// visited topics AND people persist in localStorage and surface in the
// empty-query state so opening the palette without a query is itself a
// useful jump-list.
//
// Mounts globally in App.tsx. Replaces the previous /how-to-only
// version PLUS the App's old "⌘K → /records search" shortcut.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CATEGORIES, TOPICS } from "../lib/howToTopics";
import type { Topic } from "../lib/howToTypes";
import { searchTopics } from "../lib/howToSearch";
import { quickFindPeople, type TodayQuickFindResult } from "../api";
import { havocTier } from "../lib/commentary";

// localStorage keys for two independent recents lists. Stored as id
// arrays in most-recent-first order. We bump a small fixed cap on
// each so the palette stays fast even after months of use.
const RECENT_TOPICS_KEY = "njrc.howto.recentTopics";
const RECENT_PEOPLE_KEY = "njrc.cp.recentPeople";
const MAX_RECENT_TOPICS = 8;
const MAX_RECENT_PEOPLE = 8;

/** Programmatically open the command palette from anywhere in the app.
 *  Useful for header buttons + onboarding hints that want to demo the
 *  search without requiring the user to discover the keyboard shortcut. */
export function openCommandPalette(): void {
  window.dispatchEvent(new CustomEvent("njrc:open-palette"));
}

/** Push a topic onto the recently-visited list. Called from
 *  HowToTopicPage on mount. */
export function recordTopicVisit(id: string): void {
  pushRecent(RECENT_TOPICS_KEY, id, MAX_RECENT_TOPICS);
}

/** Push a person onto the recently-visited list. Called from
 *  PersonPage on mount so the palette's empty state always shows
 *  "where you've been". */
export function recordPersonVisit(jobId: string): void {
  pushRecent(RECENT_PEOPLE_KEY, jobId, MAX_RECENT_PEOPLE);
}

function pushRecent(key: string, id: string, max: number): void {
  try {
    const raw = localStorage.getItem(key);
    const list: string[] = raw ? JSON.parse(raw) : [];
    const next = [id, ...list.filter((x) => x !== id)].slice(0, max);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Quota / private-mode failures are fine - palette just won't have recent.
  }
}

function readRecent(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

interface QuickAction {
  kind: "action";
  id: string;
  label: string;
  hint: string;
  icon: string;
  to: string;
}

// Quick actions are pinned navigation. They're always available in the
// empty-query state under "Actions" and they ALSO surface as search hits
// when the query matches their label/hint - so a junior staffer typing
// "stats" or "log" lands on the same actions a power user reaches via
// muscle memory.
const QUICK_ACTIONS: QuickAction[] = [
  // Top-level routes - same order as the App nav.
  { kind: "action", id: "go-today",   label: "Go to Today",       hint: "Daily triage: warrants, follow-ups, court dates", icon: "🗓", to: "/today" },
  { kind: "action", id: "go-lookup",  label: "Add a person",      hint: "New intake: search MCCS by name + DOB",          icon: "＋", to: "/lookup" },
  { kind: "action", id: "go-records", label: "Browse all people", hint: "Full ledger of captured records",                  icon: "👥", to: "/records" },
  { kind: "action", id: "go-story",   label: "Story (cohort narratives)", hint: "Pick a cast and read their composite story", icon: "📖", to: "/story" },
  { kind: "action", id: "go-stats",   label: "Stats",             hint: "Aggregate dashboards across the cohort",          icon: "📊", to: "/stats" },
  { kind: "action", id: "go-howto",   label: "HOW TO handbook",   hint: "Staff resources: birth certs, court directory, etc.", icon: "📚", to: "/how-to" },
  { kind: "action", id: "go-ideas",   label: "Ideas",             hint: "Roadmap of feature ideas to vote on",              icon: "💡", to: "/ideas" },
  { kind: "action", id: "go-log",     label: "Activity log",      hint: "Per-job scrape history + retry controls",          icon: "📋", to: "/log" },
  // Special bookmarks.
  { kind: "action", id: "open-court-directory", label: "Find an NJ municipal court", hint: "Map + 576 courts with contact details", icon: "⚖️", to: "/how-to" },
  { kind: "action", id: "open-international",   label: "Birth certificate from another country…", hint: "Country picker", icon: "🌎", to: "/how-to/international-birth-certificates" },
  { kind: "action", id: "open-states",          label: "Birth certificate from another US state…", hint: "State picker",  icon: "🏛️", to: "/how-to/us-states-birth-certificates" },
];

type Result =
  | { kind: "person"; person: TodayQuickFindResult }
  | { kind: "topic"; topic: Topic }
  | { kind: "category"; id: string; title: string; blurb: string }
  | QuickAction;

export function CommandPalette(): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [people, setPeople] = useState<TodayQuickFindResult[]>([]);
  const [recentPeopleData, setRecentPeopleData] = useState<TodayQuickFindResult[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  // Sequence id stamps each in-flight people-search request so we can
  // discard a slow response if a faster newer one has already returned.
  const seqRef = useRef(0);

  // Global ⌘K / Ctrl+K to open. Single source of truth - App.tsx no
  // longer also listens for ⌘K (the previous "go to /records search"
  // behavior is now reachable via "Browse all people" in the action list).
  // Also listens for a custom "njrc:open-palette" event so any UI element
  // anywhere in the app can open the palette without lifting state up.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        // Don't fight a focused input that's also using ⌘K (rare; native
        // browser doesn't bind ⌘K to anything important on most pages).
        e.preventDefault();
        setOpen((v) => !v);
        setQuery("");
        setActiveIdx(0);
      } else if (e.key === "/" && !open) {
        // "/" as a secondary opener - only when not in a form field.
        const t = e.target as HTMLElement | null;
        const inField =
          t?.tagName === "INPUT" ||
          t?.tagName === "TEXTAREA" ||
          t?.isContentEditable;
        if (inField) return;
        e.preventDefault();
        setOpen(true);
        setQuery("");
        setActiveIdx(0);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    function onOpenEvent() {
      setOpen(true);
      setQuery("");
      setActiveIdx(0);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("njrc:open-palette", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("njrc:open-palette", onOpenEvent);
    };
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // When the palette opens, prime two pieces of state:
  //   1. Cached recent topics - read once, no JSON.parse on every keystroke.
  //   2. Recent people, fetched ONCE so we can render their actual
  //      names/DOBs in the empty state. We use the same /api/today/search
  //      endpoint with the names from localStorage; if that's slow we
  //      degrade gracefully (just show the action list).
  const recentTopics = useMemo<Topic[]>(() => {
    if (!open) return [];
    return readRecent(RECENT_TOPICS_KEY)
      .map((id) => TOPICS.find((t) => t.id === id))
      .filter(Boolean) as Topic[];
  }, [open]);

  useEffect(() => {
    if (!open) {
      setRecentPeopleData([]);
      return;
    }
    const ids = readRecent(RECENT_PEOPLE_KEY);
    if (ids.length === 0) return;
    let cancelled = false;
    // /api/today/search returns matches by name; use ids to lookup via
    // a small batch endpoint would be cleaner, but for now we issue a
    // listJobs equivalent via the same search endpoint with the empty
    // query won't work - easiest pragmatic path: fetch /api/jobs once
    // and filter in memory. That endpoint already exists and is light.
    fetch("/api/jobs", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { jobs?: Array<{ id: string; first_name: string; last_name: string; date_of_birth: string; status: string; havoc_score: number | null; completed_at: string | null; created_at: string }> } | null) => {
        if (cancelled || !data?.jobs) return;
        const map = new Map(data.jobs.map((j) => [j.id, j]));
        const out: TodayQuickFindResult[] = [];
        for (const id of ids) {
          const j = map.get(id);
          if (!j) continue;
          out.push({
            jobId: j.id,
            name: `${j.first_name} ${j.last_name}`.trim(),
            dob: j.date_of_birth,
            status: j.status,
            havocScore: j.havoc_score,
            touched: j.completed_at ?? j.created_at,
          });
        }
        setRecentPeopleData(out);
      })
      .catch(() => { /* degrade silently */ });
    return () => { cancelled = true; };
  }, [open]);

  // Debounced people search. Runs as the user types - 200ms debounce
  // balances "feels live" against not hammering the server. The query
  // sent to the server is the SCOPED query (with the @/›/§/¶ prefix
  // stripped) so `@marcus` searches people for "marcus".
  useEffect(() => {
    // Need to compute scopedQuery synchronously here since the useMemo
    // above hasn't necessarily run yet on this render. Cheap; same logic.
    const raw = query.trimStart();
    let q: string;
    const ch = raw[0];
    if (ch === "@" || ch === ">" || ch === "§" || ch === "¶") {
      q = raw.slice(1).trim();
    } else {
      q = raw.trim();
    }
    if (!q) {
      setPeople([]);
      setPeopleLoading(false);
      setPeopleError(null);
      return;
    }
    setPeopleLoading(true);
    setPeopleError(null);
    const mySeq = ++seqRef.current;
    const t = window.setTimeout(async () => {
      try {
        const res = await quickFindPeople(q);
        if (mySeq !== seqRef.current) return; // stale
        setPeople(res);
      } catch (err) {
        if (mySeq !== seqRef.current) return;
        setPeople([]);
        setPeopleError(err instanceof Error ? err.message : "Search failed");
      } finally {
        if (mySeq === seqRef.current) setPeopleLoading(false);
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [query]);

  // Scope-prefix parsing - typing `>` filters to actions only,
  // `@` to people, `§` to topics, `¶` to categories. The sigil
  // vocabulary matches the per-row glyphs (sigilFor()) so the
  // scope is discoverable from the row marks themselves. The
  // leading sigil + space is stripped from the search query
  // before matching, so `@marcus` searches people for "marcus".
  type ScopeKind = "person" | "topic" | "category" | "action" | "all";
  const { scope, scopedQuery } = useMemo<{ scope: ScopeKind; scopedQuery: string }>(() => {
    const raw = query.trimStart();
    if (raw.length === 0) return { scope: "all", scopedQuery: "" };
    const ch = raw[0];
    if (ch === "@") return { scope: "person",   scopedQuery: raw.slice(1).trim() };
    if (ch === ">") return { scope: "action",   scopedQuery: raw.slice(1).trim() };
    if (ch === "§") return { scope: "topic",    scopedQuery: raw.slice(1).trim() };
    if (ch === "¶") return { scope: "category", scopedQuery: raw.slice(1).trim() };
    return { scope: "all", scopedQuery: raw.trim() };
  }, [query]);

  // Build the unified results list. Empty state surfaces recents first,
  // then quick actions. Searching combines: people > topics > categories
  // > matching actions. When a scope prefix is active, only that kind
  // shows up.
  const sections: Array<{ label: string; rows: Result[] }> = useMemo(() => {
    const q = scopedQuery.toLowerCase();
    if (!q) {
      const out: Array<{ label: string; rows: Result[] }> = [];
      if (recentPeopleData.length > 0) {
        out.push({ label: "Recent people", rows: recentPeopleData.slice(0, 5).map((p) => ({ kind: "person" as const, person: p })) });
      }
      out.push({ label: "Actions", rows: QUICK_ACTIONS.slice(0, 5).map((a) => a) });
      if (recentTopics.length > 0) {
        out.push({ label: "Recent topics", rows: recentTopics.slice(0, 4).map((t) => ({ kind: "topic" as const, topic: t })) });
      }
      return out;
    }
    const out: Array<{ label: string; rows: Result[] }> = [];
    const wantsPeople = scope === "all" || scope === "person";
    const wantsTopic = scope === "all" || scope === "topic";
    const wantsCategory = scope === "all" || scope === "category";
    const wantsAction = scope === "all" || scope === "action";
    if (wantsPeople && people.length > 0) {
      out.push({ label: "People", rows: people.slice(0, 6).map((p) => ({ kind: "person" as const, person: p })) });
    }
    if (wantsTopic) {
      const topicHits = searchTopics(TOPICS, q).slice(0, 8);
      if (topicHits.length > 0) {
        out.push({ label: "How to", rows: topicHits.map((t) => ({ kind: "topic" as const, topic: t })) });
      }
    }
    if (wantsCategory) {
      const catHits = CATEGORIES.filter(
        (c) => c.title.toLowerCase().includes(q) || c.blurb.toLowerCase().includes(q),
      );
      if (catHits.length > 0) {
        out.push({ label: "Categories", rows: catHits.map((c) => ({ kind: "category" as const, id: c.id, title: c.title, blurb: c.blurb })) });
      }
    }
    if (wantsAction) {
      const actionHits = QUICK_ACTIONS.filter(
        (a) => a.label.toLowerCase().includes(q) || a.hint.toLowerCase().includes(q),
      );
      if (actionHits.length > 0) {
        out.push({ label: "Actions", rows: actionHits });
      }
    }
    return out;
  }, [scope, scopedQuery, recentPeopleData, recentTopics, people]);

  // Flat list (preserves section order) for keyboard nav. Users navigate
  // through visual rows, not sections.
  const flat = useMemo(() => sections.flatMap((s) => s.rows), [sections]);

  // Keep activeIdx in range when results change.
  useEffect(() => {
    if (activeIdx >= flat.length) setActiveIdx(0);
  }, [flat.length, activeIdx]);

  function execute(r: Result) {
    setOpen(false);
    if (r.kind === "topic") navigate(`/how-to/${r.topic.id}`);
    else if (r.kind === "category") navigate(`/how-to?cat=${r.id}`);
    else if (r.kind === "person") navigate(`/records/${r.person.jobId}`);
    else if (r.kind === "action") navigate(r.to);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = flat[activeIdx];
      if (r) execute(r);
    }
  }

  if (!open) return null;

  // Build a flat-index-aware section render so we can highlight the
  // active row across sections.
  let runningIdx = 0;
  const isPeopleSearch = !!query.trim() && peopleLoading;

  return (
    <>
      {/* Backdrop - warm scrim with a 10px blur. DESIGN.md No-
          Glassmorphism Rule allows the command palette as the single
          named exception: the blur is purposeful here, framing the
          masthead as the focal point and pushing the page into a
          deliberate out-of-focus background. */}
      <div
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(26, 19, 14, 0.45)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          zIndex: 60,
          animation: "cpFade 160ms ease-out",
        }}
      />
      {/* Palette - borderless. The shape is defined by a layered close +
          far shadow rather than a 1px hairline, which on the cream
          card surface in light mode read as a stark white outline.
          Editorial cards lift via shadow, not via stroke. */}
      <div
        role="dialog"
        aria-label="Command palette"
        style={{
          position: "fixed",
          top: "10vh",
          left: "50%",
          transform: "translateX(-50%)",
          width: "min(720px, 92vw)",
          maxHeight: "75vh",
          background: "var(--color-bg-card)",
          border: "none",
          borderRadius: 14,
          zIndex: 70,
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 4px 14px rgba(0, 0, 0, 0.18), 0 24px 56px -12px rgba(0, 0, 0, 0.35)",
          animation: "cpRise 240ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <style>{`
          @keyframes cpFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes cpRise {
            from { opacity: 0; transform: translateX(-50%) translateY(-12px) scale(0.98); }
            to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
          }
        `}</style>

        {/* Masthead - drenched in deep wine for both themes. Reads as the
            magazine masthead at the top of the contents page; the body
            below stays light/dark per theme. Cream "NJRC · The Index"
            eyebrow, cream Fraunces input, cream chrome - same brand
            energy as the page hero, scoped to the search action. */}
        <div
          className="cp-masthead"
          style={{
            padding: "14px 22px 14px",
            background: "linear-gradient(155deg, #5A1F2E 0%, #3A1018 100%)",
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
          }}
        >
          {scope !== "all" && (
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 13,
                color: "rgba(248, 210, 218, 0.78)",
                marginBottom: 6,
                fontVariationSettings: '"opsz" 24, "SOFT" 40',
              }}
            >
              looking up · {scope === "person" ? "people" : scope === "topic" ? "how-to" : scope === "category" ? "categories" : "actions"} only
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(248, 210, 218, 0.70)"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.3-4.3" />
            </svg>
            <input
              ref={inputRef}
              className="cp-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Name, AKA, DOB, or docket #"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 22,
                // Editorial input pattern: italic Fraunces with an
                // empty value (the masthead invites you in), upright
                // Fraunces weight 600 while typing (you're now writing
                // INTO the masthead). The opsz axis sits at 24 (text-
                // tier optical, correct for 22px) - not 60, which was
                // display-tier and read as "decorative-script in an
                // input I type into all day."
                fontWeight: query.length === 0 ? 500 : 600,
                fontStyle: query.length === 0 ? "italic" : "normal",
                lineHeight: 1.15,
                letterSpacing: "-0.005em",
                color: "#FFFFFF",
                fontFamily: "var(--font-display)",
                fontVariationSettings: '"opsz" 24, "SOFT" 40',
              }}
            />
            <style>{`
              .cp-input::placeholder { color: rgba(255, 255, 255, 0.7) !important; opacity: 1 !important; }
            `}</style>
            {isPeopleSearch && (
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "var(--color-gold-cream-hi)",
                  animation: "cpPulse 1.2s ease-in-out infinite",
                }}
                title="Searching people…"
              />
            )}
            <kbd
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "rgba(248, 210, 218, 0.78)",
                border: "1px solid rgba(248, 210, 218, 0.30)",
                borderRadius: 4,
                padding: "2px 6px",
                background: "rgba(248, 210, 218, 0.10)",
              }}
            >
              ESC
            </kbd>
          </div>
          <div
            style={{
              marginLeft: 34,
              marginTop: 7,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              letterSpacing: "0.04em",
              color: "rgba(248, 210, 218, 0.70)",
            }}
          >
            people search includes intake aliases and docket fragments · @ people · › actions
          </div>
        </div>
        <style>{`@keyframes cpPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>

        {/* Results */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 8,
          }}
        >
          {!query.trim() && sections.length === 0 && (
            <div
              style={{
                padding: "24px 16px",
                textAlign: "center",
                color: "var(--color-text-dim)",
                fontSize: 14,
              }}
            >
              Start typing to search people, topics, and actions.
            </div>
          )}
          {query.trim() && peopleError && !peopleLoading && (
            <div
              style={{
                padding: "20px 16px",
                color: "var(--color-rose-hi)",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              People search unavailable. Try again.
              <div style={{ fontSize: 12, marginTop: 8, color: "var(--color-text-muted)" }}>
                {peopleError}
              </div>
            </div>
          )}
          {query.trim() && flat.length === 0 && !peopleLoading && !peopleError && (
            <div
              style={{
                padding: "20px 16px",
                color: "var(--color-text-dim)",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              No matches for "{query}".
              <div style={{ fontSize: 12, marginTop: 8, opacity: 0.85 }}>
                People search uses name or date-of-birth · How-to search uses topic title or summary.
              </div>
            </div>
          )}
          {sections.map((section) => {
            const startIdx = runningIdx;
            runningIdx += section.rows.length;
            const compactActions = !query.trim() && section.label === "Actions";
            return (
              <div key={section.label}>
                <SectionHeader>{section.label}</SectionHeader>
                {compactActions ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "2px 10px 8px" }}>
                    {section.rows.map((r, i) => {
                      const flatIdx = startIdx + i;
                      const active = flatIdx === activeIdx;
                      return (
                        <button
                          key={resultKey(r)}
                          type="button"
                          className="cp-chip"
                          onMouseEnter={() => setActiveIdx(flatIdx)}
                          onClick={() => execute(r)}
                          aria-current={active ? "true" : undefined}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 7,
                            minHeight: 34,
                            padding: "7px 10px",
                            borderRadius: 8,
                            border: active ? "1px solid #8C1F2F" : "1px solid var(--color-line)",
                            background: active ? "#8C1F2F" : "var(--color-bg-card-hi)",
                            color: active ? "#F2E9D6" : "var(--color-text-muted)",
                            fontFamily: "var(--font-display)",
                            fontStyle: "italic",
                            fontSize: 14,
                            fontWeight: 650,
                            cursor: "pointer",
                          }}
                        >
                          <span aria-hidden style={{ fontFamily: "var(--font-mono)", fontStyle: "normal", fontSize: 12 }}>›</span>
                          {r.kind === "action" ? r.label.replace(/^Go to /, "") : ""}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  section.rows.map((r, i) => {
                    const flatIdx = startIdx + i;
                    return (
                      <ResultRow
                        key={resultKey(r)}
                        result={r}
                        active={flatIdx === activeIdx}
                        onMouseEnter={() => setActiveIdx(flatIdx)}
                        onClick={() => execute(r)}
                      />
                    );
                  })
                )}
              </div>
            );
          })}
        </div>

        {/* Footer - magazine colophon. Replaces the SaaS cheat-sheet
            ("↑↓ navigate · ↵ open · esc close · 14 results") with a
            Fraunces-italic prose line on the left and a tiny meta on
            the right, both in the editorial register. */}
        <div
          style={{
            padding: "12px 22px",
            borderTop: "1px solid var(--color-line)",
            display: "flex",
            gap: 16,
            alignItems: "baseline",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 13,
              fontVariationSettings: '"opsz" 24, "SOFT" 30',
              color: "var(--color-text-muted)",
              lineHeight: 1.4,
            }}
          >
            type to search · @ for people, § for how-to, ¶ for categories, › for actions · enter to open
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 12,
              fontVariationSettings: '"opsz" 24, "SOFT" 30',
              color: "var(--color-text-dim)",
            }}
          >
            {!query.trim() && sections.length > 0
              ? `${flat.length} ${flat.length === 1 ? "entry" : "entries"} on the desk`
              : query.trim()
                ? peopleLoading
                  ? "searching the cohort…"
                  : `${flat.length} ${flat.length === 1 ? "match" : "matches"}`
                : "the desk is open"}
          </span>
        </div>
      </div>
    </>
  );
}

function resultKey(r: Result): string {
  if (r.kind === "topic") return `t:${r.topic.id}`;
  if (r.kind === "category") return `c:${r.id}`;
  if (r.kind === "person") return `p:${r.person.jobId}`;
  return `a:${r.id}`;
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  // Magazine "section" header - Fraunces italic lowercase. At 15px the
  // opsz axis should be 24 (text-tier optical), not 36 (display-tier).
  // The DESIGN.md opsz ladder: opsz 24 for ≤16px, opsz 36 for 17-25px,
  // opsz 60 for 26-50px, opsz 96+ for hero. Replaces the previous Inter
  // UPPERCASE 0.14em tracking which read as a SaaS-eyebrow.
  const text = typeof children === "string" ? children.toLowerCase() : children;
  return (
    <div
      style={{
        fontFamily: "var(--font-display)",
        fontStyle: "italic",
        fontSize: 15,
        fontWeight: 500,
        fontVariationSettings: '"opsz" 24, "SOFT" 40',
        letterSpacing: "-0.005em",
        color: "var(--color-text-muted)",
        padding: "12px 12px 4px",
      }}
    >
      {text}
    </div>
  );
}

// All type labels render in the same dim-ink color now. Previously
// each kind got its own brand color (cherry/sage/gold/ink-dim) which
// made the right rail compete with itself - three brand-system colors
// flashing at every glance. The kind is differentiated by the mono
// sigil on the left + the typeLabel text on the right; the color
// channel is freed up for the active-row treatment.
function typeLabelColor(_kind: Result["kind"]): string {
  return "var(--color-text-dim)";
}

// Mono sigil per result kind - replaces the emoji-style icons that
// blasted color into the system. Matches the editorial-typographic
// register: a tiny printer's mark in the corner of each row.
function sigilFor(kind: Result["kind"]): string {
  switch (kind) {
    case "person":   return "›";
    case "topic":    return "§";
    case "category": return "¶";
    default:         return "→";
  }
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        color: "var(--color-text-muted)",
        border: "1px solid var(--color-line-hi)",
        borderRadius: 3,
        padding: "1px 5px",
        background: "var(--color-bg-card-hi)",
      }}
    >
      {children}
    </kbd>
  );
}

function ResultRow({
  result,
  active,
  onMouseEnter,
  onClick,
}: {
  result: Result;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  let title: string;
  let subtitle: string;
  let typeLabel: string;
  let trailing: React.ReactNode = null;
  if (result.kind === "topic") {
    title = result.topic.title;
    subtitle = result.topic.summary;
    typeLabel = "how to";
  } else if (result.kind === "category") {
    title = result.title;
    subtitle = result.blurb;
    typeLabel = "category";
  } else if (result.kind === "person") {
    title = result.person.name;
    const dob = result.person.dob ? `DOB ${result.person.dob}` : "";
    const status =
      result.person.status === "completed" ? "" :
      result.person.status === "queued" ? "queued" :
      result.person.status === "running" ? "scraping…" :
      result.person.status === "waiting_captcha" ? "captcha pending" :
      result.person.status === "error" ? "error" : result.person.status;
    const match =
      result.person.matchedField === "aka" && result.person.matchedValue
        ? `matched AKA: ${result.person.matchedValue}`
        : result.person.matchedField === "docket" && result.person.matchedValue
          ? `matched docket: ${result.person.matchedValue}`
          : result.person.matchedField === "dob"
            ? "matched DOB"
            : "";
    subtitle = [dob, match, status].filter(Boolean).join(" · ");
    typeLabel = "person";
    const tier = havocTier(result.person.havocScore ?? null);
    if (tier && result.person.havocScore !== null) {
      trailing = (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            fontWeight: 700,
            color: tier.textHi,
            background: "color-mix(in oklab, " + tier.textHi + " 15%, transparent)",
            border: "1px solid color-mix(in oklab, " + tier.textHi + " 35%, transparent)",
            borderRadius: 999,
            padding: "2px 8px",
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
          }}
          title={`Havoc score: ${result.person.havocScore} (${tier.label})`}
        >
          {result.person.havocScore}
        </span>
      );
    }
  } else {
    title = result.label;
    subtitle = result.hint;
    typeLabel = "action";
  }
  const sigil = sigilFor(result.kind);
  const labelColor = typeLabelColor(result.kind);
  // Active row: cherry-tinted background instead of generic bg-card-hi.
  // The brand finally lands where the eye is going. Title text shifts
  // to cherry-hi too so the active row reads as "this is the one."
  // Inactive rows get a hairline bottom separator so the list reads
  // as a contents-page table, not a hovering set of buttons.
  const activeBg = "color-mix(in oklab, var(--color-hero-bg) 10%, var(--color-bg-card))";
  const activeBorder = "color-mix(in oklab, var(--color-hero-bg) 30%, transparent)";
  return (
    <button
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      data-result-type={result.kind}
      className={active ? "cp-row cp-row-active" : "cp-row"}
      style={{
        width: "100%",
        textAlign: "left",
        background: active ? activeBg : "transparent",
        border: active
          ? `1px solid ${activeBorder}`
          : "1px solid transparent",
        borderBottom: active
          ? `1px solid ${activeBorder}`
          : "1px solid var(--color-line)",
        borderRadius: 8,
        padding: "12px 14px",
        display: "grid",
        gridTemplateColumns: "auto 1fr auto auto",
        gap: 14,
        alignItems: "center",
        cursor: "pointer",
        transition: "background 80ms ease, border-color 80ms ease",
      }}
    >
      <span
        aria-hidden
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 16,
          lineHeight: 1,
          fontWeight: 500,
          color: active ? labelColor : "var(--color-text-dim)",
          width: 14,
          textAlign: "center",
          flexShrink: 0,
          transition: "color 80ms ease",
        }}
      >
        {sigil}
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          className="cp-row-title"
          style={{
            display: "block",
            // Face encoding: person + topic titles render in Fraunces
            // (the editorial register), action + category rows in
            // Inter (the utility register). The eye learns the kind
            // from the face within a glance - no right-rail color
            // routing needed.
            fontFamily:
              result.kind === "person" || result.kind === "topic"
                ? "Fraunces, var(--font-serif), serif"
                : "var(--font-body)",
            fontStyle:
              result.kind === "person" || result.kind === "topic" ? "normal" : "normal",
            fontVariationSettings:
              result.kind === "person" || result.kind === "topic"
                ? '"opsz" 24, "SOFT" 40'
                : undefined,
            fontSize:
              result.kind === "person" || result.kind === "topic" ? 17 : 15.5,
            fontWeight:
              result.kind === "person" || result.kind === "topic" ? 500 : 600,
            letterSpacing:
              result.kind === "person" || result.kind === "topic" ? "-0.005em" : undefined,
            color: active ? "var(--color-cp-active-title)" : "var(--color-text)",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            transition: "color 80ms ease",
          }}
        >
          {title}
        </span>
        {subtitle && (
          <span
            style={{
              display: "block",
              fontSize: 13,
              color: "var(--color-text-dim)",
              lineHeight: 1.4,
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </span>
        )}
      </span>
      <span style={{ flexShrink: 0 }}>{trailing}</span>
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: 13,
          fontWeight: 500,
          fontVariationSettings: '"opsz" 24, "SOFT" 30',
          letterSpacing: "-0.005em",
          color: labelColor,
          flexShrink: 0,
        }}
      >
        {typeLabel}
      </span>
    </button>
  );
}
